// src/pages/employeeCommission/components/ProductLevelSingleModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Info } from 'lucide-react';
import { getProducts } from '../../../services/api/productSearch';
import { upsertCommissionConfig } from '../../../services/api/employeeCommission';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './PageModals.module.css';

const ProductLevelSingleModal = ({ onClose, onSaved, existingConfig = null }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const isEdit = !!existingConfig;

  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [form, setForm] = useState({
    productId: existingConfig?.productId || '',
    confirmCommission: existingConfig?.confirmCommission ?? '',
    createCommission: existingConfig?.createCommission ?? '',
    calculationBasis: existingConfig?.calculationBasis || 'order_count',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  const handleProductSearch = async (val) => {
    setProductSearch(val);
    if (val.length < 2) { setProducts([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        setIsLoadingProducts(true);
        const res = await getProducts({ search: val, limit: 50 });
        setProducts(res?.products || []);
      } catch {
        setProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    }, 400);
  };

  const validate = () => {
    const errs = {};
    if (!form.productId) errs.productId = t('employeeCommission.selectProductRequired');
    if (form.confirmCommission === '') errs.confirmCommission = t('employeeCommission.confirmCommissionRequired');
    if (form.createCommission === '') errs.createCommission = t('employeeCommission.createCommissionRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await upsertCommissionConfig({
        userId: null,
        productId: parseInt(form.productId),
        confirmCommission: parseFloat(form.confirmCommission),
        createCommission: parseFloat(form.createCommission),
        calculationBasis: form.calculationBasis,
      });
      success(t('employeeCommission.configSaved'));
      onSaved();
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || t('employeeCommission.configSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const basisLabel = (key) => {
    const map = {
      order_count: t('employeeCommission.orderCount'),
      order_quantity: t('employeeCommission.orderQuantity'),
      order_value: t('employeeCommission.orderValue'),
    };
    return map[key] || key;
  };

  const commissionSuffix = form.calculationBasis === 'order_value' ? '%' : 'LKR';

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitle}>
              {isEdit ? t('employeeCommission.editProductConfig') : t('employeeCommission.addProductConfig')}
            </h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}><X size={16} /></button>
        </div>

        <div className={styles.modalBody}>
          {!isEdit && (
            <div className={styles.formGroup} style={{ marginBottom: '1rem' }}>
              <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                {t('employeeCommission.selectProduct')}
              </label>
              <div className={styles.searchWrapper}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  placeholder={t('employeeCommission.searchProduct')}
                  value={productSearch}
                  onChange={(e) => handleProductSearch(e.target.value)}
                />
              </div>
              {products.length > 0 && (
                <select
                  className={`${styles.formSelect} ${errors.productId ? styles.error : ''}`}
                  value={form.productId}
                  onChange={(e) => setForm({ ...form, productId: e.target.value })}
                  style={{ marginTop: '0.5rem' }}
                >
                  <option value="">-- Select --</option>
                  {products.map((p) => (
                    <option key={p.productId || p.id} value={p.productId || p.id}>
                      {p.displayName || p.name} {p.sku ? `(${p.sku})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {errors.productId && <span className={styles.formError}>{errors.productId}</span>}
            </div>
          )}

          {isEdit && (
            <div className={styles.infoBox}>
              <Info size={14} />
              <p>Editing commission for product: <strong>{existingConfig.product?.name}</strong></p>
            </div>
          )}

          <div className={styles.formGroup} style={{ marginBottom: '1rem' }}>
            <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
              {t('employeeCommission.calculationBasisLabel')}
            </label>
            <select
              className={styles.formSelect}
              value={form.calculationBasis}
              onChange={(e) => setForm({ ...form, calculationBasis: e.target.value })}
            >
              {['order_count', 'order_quantity', 'order_value'].map((v) => (
                <option key={v} value={v}>{basisLabel(v)}</option>
              ))}
            </select>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                {t('employeeCommission.confirmCommissionLabel')} ({commissionSuffix})
              </label>
              <input
                type="number" min="0" step="0.01"
                className={`${styles.formInput} ${errors.confirmCommission ? styles.error : ''}`}
                value={form.confirmCommission}
                onChange={(e) => setForm({ ...form, confirmCommission: e.target.value })}
                placeholder="0.00"
              />
              {errors.confirmCommission && <span className={styles.formError}>{errors.confirmCommission}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                {t('employeeCommission.createCommissionLabel')} ({commissionSuffix})
              </label>
              <input
                type="number" min="0" step="0.01"
                className={`${styles.formInput} ${errors.createCommission ? styles.error : ''}`}
                value={form.createCommission}
                onChange={(e) => setForm({ ...form, createCommission: e.target.value })}
                placeholder="0.00"
              />
              {errors.createCommission && <span className={styles.formError}>{errors.createCommission}</span>}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>
            {t('employeeCommission.cancel')}
          </button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? <><div className={styles.spinner} />{t('employeeCommission.saving')}</> : t('employeeCommission.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductLevelSingleModal;